import { createSlice } from '@reduxjs/toolkit';
import {
  ValidationSpecialistSubjectsState,
  ValidationSpecialistSubjectsListFetchAction,
  ValidationSpecialistSubjectsListFetchSuccessAction,
  ValidationSpecialistSubjectDetailsFetchAction,
  ValidationSpecialistSubjectDetailsFetchSuccessAction,
  ValidationSpecialistVisitUploadsFetchAction,
  ValidationSpecialistVisitUploadsFetchSuccessAction,
  ValidationSpecialistTrialsFetchAction,
  ValidationSpecialistTrialsFetchSuccessAction,
  ValidationSpecialistContextFetchAction,
  ValidationSpecialistContextFetchSuccessAction,
} from './ValidationSpecialistSubjects.types';
import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

const initialState: ValidationSpecialistSubjectsState = {
  loading: false,
  error: undefined,
  data: undefined,
  subjectDetails: undefined,
  uploads: [],
  uploadsLoading: false,
  trials: undefined,
  trialsLoading: false,
  context: undefined,
  contextLoading: false,
  search: '',
};

const { actReject, actReq, actResolve, act } =
  makeActionCreator<ValidationSpecialistSubjectsState>();

export const ValidationSpecialistSubjectsSlice = createSlice({
  name: 'ValidationSpecialistSubjects',
  initialState,
  reducers: {
    fetchValidationSpecialistSubjectsList: actReq<ValidationSpecialistSubjectsListFetchAction['payload']>(),
    fetchValidationSpecialistSubjectsListSuccess:
      actResolve<ValidationSpecialistSubjectsListFetchSuccessAction['payload']>(),
    fetchValidationSpecialistSubjectsListFailure: actReject(),

    fetchValidationSpecialistSubjectDetails: actReq<ValidationSpecialistSubjectDetailsFetchAction['payload']>(),
    fetchValidationSpecialistSubjectDetailsSuccess: act<
      ValidationSpecialistSubjectDetailsFetchSuccessAction['payload']
    >((subjData, state) => ({
      ...state,
      subjectDetails: subjData,
      loading: false,
    })),
    fetchValidationSpecialistSubjectDetailsFailure: actReject(),

    clearSubjectDetails: state => {
      state.subjectDetails = undefined;
    },

    fetchValidationSpecialistVisitUploads: actReq<ValidationSpecialistVisitUploadsFetchAction['payload']>('uploadsLoading'),
    fetchValidationSpecialistVisitUploadsSuccess: act<
      ValidationSpecialistVisitUploadsFetchSuccessAction['payload']
    >((payload, state) => ({
        ...state,
        uploads: payload.uploads,
        uploadsLoading: false,
    })),
    fetchValidationSpecialistVisitUploadsFailure: actReject(),

    setSearchValue: act<string>(searchValue => ({
      search: searchValue,
    })),

    fetchTrials: act<ValidationSpecialistTrialsFetchAction['payload']>(
      (_, state) => ({
        ...state,
        trialsLoading: true,
      })
    ),
    fetchTrialsSuccess: act<ValidationSpecialistTrialsFetchSuccessAction['payload']>(
      (trials, state) => ({
        ...state,
        trials,
        trialsLoading: false,
      })
    ),
    fetchTrialsFailure: act<Error>((error, state) => ({
      ...state,
      error,
      trialsLoading: false,
    })),

    fetchContext: actReq<ValidationSpecialistContextFetchAction['payload']>(),
    fetchContextSuccess: act<ValidationSpecialistContextFetchSuccessAction['payload']>(
      (context, state) => ({
        ...state,
        context,
        contextLoading: false,
      })
    ),
    fetchContextFailure: act<Error>((error, state) => ({
      ...state,
      error,
      contextLoading: false,
    })),
  },
});

export const {
  fetchValidationSpecialistSubjectsList,
  fetchValidationSpecialistSubjectsListSuccess,
  fetchValidationSpecialistSubjectsListFailure,
  fetchValidationSpecialistSubjectDetails,
  fetchValidationSpecialistSubjectDetailsSuccess,
  fetchValidationSpecialistSubjectDetailsFailure,
  clearSubjectDetails,
  fetchValidationSpecialistVisitUploads,
  fetchValidationSpecialistVisitUploadsSuccess,
  fetchValidationSpecialistVisitUploadsFailure,
  setSearchValue,
  fetchTrials,
  fetchTrialsSuccess,
  fetchTrialsFailure,
  fetchContext,
  fetchContextSuccess,
  fetchContextFailure,
} = ValidationSpecialistSubjectsSlice.actions;

export const actions = {
  ...ValidationSpecialistSubjectsSlice.actions,
};

export const useValidationSpecialistSubjectsSlice = createSliceHook(
  ValidationSpecialistSubjectsSlice.name,
  actions
);

export const selectValidationSpecialistSubjectsState = createSelector(ValidationSpecialistSubjectsSlice.name);
